import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip, Switch, TextField,
  Chip, Stack, CircularProgress, Paper, Avatar, List,
  ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ShoppingBag as CartIcon,
  Search as SearchIcon,
  CheckCircle as InStockIcon,
  Warning as OutOfStockIcon
} from '@mui/icons-material';
import api from '../api/axios';
import { color, shadow } from '../theme/tokens';
import { getImageUrl } from '../config';
import {
  PageHeader, Panel, DataTable, FormDialog, ConfirmDialog, useFeedback
} from '../components/ui';

const PRESET_BADGES = ['MUST HAVE', 'FAST CHARGE', 'GENUINE', 'TOP SELLER', 'RECOMMENDED', 'ESSENTIAL'];

const getAccessoryFallback = (name = '') => {
  const n = String(name || '').toLowerCase();
  if (n.includes('cable') || n.includes('wire')) return getImageUrl('/uploads/products/TiiP2MFI_1.jpg');
  if (n.includes('neckband') || n.includes('ear') || n.includes('bud')) return getImageUrl('/uploads/products/TiiN3_1.jpg');
  if (n.includes('adapt') || n.includes('charg')) return getImageUrl('/uploads/products/194252156988_1.webp');
  return getImageUrl('/uploads/products/TiiP7_1.jpg');
};

export default function CartAddons() {
  const { success, error } = useFeedback();
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // Product selection inside modal
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [badgeText, setBadgeText] = useState('MUST HAVE');
  const [customTitle, setCustomTitle] = useState('');
  const [sortOrder, setSortOrder] = useState(1);

  // Delete confirm
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchAddons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/cart-addons');
      setAddons(res.data?.data || []);
    } catch (err) {
      error('Failed to load cart add-ons');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    fetchAddons();
  }, [fetchAddons]);

  // Search accessories from catalog
  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (!q || q.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await api.get('/api/admin/homepage/search-products', {
        params: { q: q.trim(), category: 'accessories', limit: 8 }
      });
      if (res.data?.success) {
        setSearchResults(res.data.data || []);
      }
    } catch (err) {
      // Ignore aborts
    } finally {
      setSearchLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingItem(null);
    setSelectedProduct(null);
    setSearchQuery('');
    setSearchResults([]);
    setBadgeText('MUST HAVE');
    setCustomTitle('');
    setSortOrder(addons.length + 1);
    setDialogOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setSelectedProduct({
      id: item.product_id,
      name: item.product_name,
      salePrice: item.sale_price,
      primaryImage: item.image_url,
      brand: item.brand
    });
    setSearchQuery('');
    setSearchResults([]);
    setBadgeText(item.badge_text || 'MUST HAVE');
    setCustomTitle(item.custom_title || '');
    setSortOrder(item.sort_order || 0);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedProduct && !editingItem) {
      error('Please select an accessory product from catalog');
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/api/admin/cart-addons/${editingItem.id}`, {
          custom_title: customTitle.trim() || null,
          badge_text: badgeText.trim().toUpperCase(),
          sort_order: Number(sortOrder) || 0,
          is_active: editingItem.is_active ? 1 : 0
        });
        success('Cart add-on updated successfully');
      } else {
        await api.post('/api/admin/cart-addons', {
          product_id: selectedProduct.id,
          custom_title: customTitle.trim() || null,
          badge_text: badgeText.trim().toUpperCase() || 'RECOMMENDED',
          sort_order: Number(sortOrder) || 0
        });
        success('Product pinned to cart impulse tray');
      }
      setDialogOpen(false);
      fetchAddons();
    } catch (err) {
      error(err.response?.data?.message || 'Failed to save cart add-on');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.patch(`/api/admin/cart-addons/${id}/toggle`);
      setAddons(prev => prev.map(a => a.id === id ? { ...a, is_active: a.is_active ? 0 : 1 } : a));
      success('Add-on status toggled');
    } catch (err) {
      error('Failed to toggle status');
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await api.delete(`/api/admin/cart-addons/${deletingId}`);
      success('Cart add-on removed');
      setDeleteConfirmOpen(false);
      setDeletingId(null);
      fetchAddons();
    } catch (err) {
      error('Failed to delete cart add-on');
    }
  };

  const activeAddons = addons.filter(a => a.is_active);

  const columns = [
    {
      key: 'sort_order',
      label: '#',
      width: 60,
      render: (_v, row) => (
        <Typography variant="body2" sx={{ fontWeight: 800, color: color.ink[400], fontFamily: 'monospace' }}>
          {String(row.sort_order).padStart(2, '0')}
        </Typography>
      )
    },
    {
      key: 'product',
      label: 'Product Details',
      render: (_v, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src={getImageUrl(row.image_url) || getAccessoryFallback(row.product_name)}
            alt={row.product_name}
            sx={{
              width: 44,
              height: 44,
              objectFit: 'contain',
              borderRadius: '8px',
              backgroundColor: '#F9FAFB',
              border: `1px solid ${color.ink[200]}`,
              p: 0.5
            }}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = getAccessoryFallback(row.product_name);
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: color.ink[900] }} noWrap>
              {row.custom_title || row.product_name}
            </Typography>
            <Typography variant="caption" sx={{ color: color.ink[500], display: 'block' }}>
              {row.brand || 'Hotspot'} • Code: {row.item_code || 'N/A'}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      key: 'badge',
      label: 'Impulse Badge',
      width: 140,
      render: (_v, row) => (
        <Chip
          label={row.badge_text}
          size="small"
          sx={{
            fontWeight: 800,
            fontSize: '0.68rem',
            backgroundColor: color.primary[50],
            color: color.primary[700],
            border: `1px solid ${color.primary[200]}`,
            letterSpacing: '0.04em'
          }}
        />
      )
    },
    {
      key: 'pricing',
      label: 'Price & MRP',
      width: 130,
      render: (_v, row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 800, color: color.ink[900] }}>
            ₹{row.sale_price?.toLocaleString('en-IN')}
          </Typography>
          {row.mrp > row.sale_price && (
            <Typography variant="caption" sx={{ color: color.ink[400], textDecoration: 'line-through' }}>
              ₹{row.mrp?.toLocaleString('en-IN')}
            </Typography>
          )}
        </Box>
      )
    },
    {
      key: 'stock',
      label: 'Stock Status',
      width: 120,
      render: (_v, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {row.stock_qty > 0 ? (
            <>
              <InStockIcon sx={{ fontSize: 16, color: '#10B981' }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#065F46' }}>
                {row.stock_qty} in stock
              </Typography>
            </>
          ) : (
            <>
              <OutOfStockIcon sx={{ fontSize: 16, color: '#EF4444' }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#991B1B' }}>
                Out of stock
              </Typography>
            </>
          )}
        </Box>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      width: 100,
      render: (_v, row) => (
        <Switch
          size="small"
          checked={Boolean(row.is_active)}
          onChange={() => handleToggle(row.id)}
          color="success"
        />
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 100,
      align: 'right',
      render: (_v, row) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title="Edit Badge / Title">
            <IconButton size="small" onClick={() => handleOpenEdit(row)}>
              <EditIcon fontSize="small" sx={{ color: color.ink[600] }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove from Tray">
            <IconButton
              size="small"
              onClick={() => {
                setDeletingId(row.id);
                setDeleteConfirmOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" sx={{ color: '#EF4444' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <PageHeader
        title="Cart Impulse Add-Ons"
        subtitle="Manage the Blinkit-style quick cross-sell accessories tray shown inside the cart drawer before checkout"
        action={
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchAddons}
              sx={{ textTransform: 'none', fontWeight: 600, borderColor: color.ink[200], color: color.ink[700], bgcolor: '#FFFFFF' }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: color.primary[600],
                '&:hover': { backgroundColor: color.primary[700] },
                boxShadow: shadow.sm
              }}
            >
              Pin Accessory
            </Button>
          </Stack>
        }
      />

      {/* ── Visual Blinkit Tray Live Preview ── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3.5,
          borderRadius: '16px',
          border: `1px solid ${color.ink[200]}`,
          backgroundColor: '#FAF9F6',
          boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CartIcon sx={{ color: color.primary[600], fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: color.ink[900], textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Live Storefront Preview — Blinkit Cart Drawer Cross-Sell Tray
            </Typography>
          </Box>
          <Chip
            label={`${activeAddons.length} Items Active in Tray`}
            size="small"
            sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#ECFDF5', color: '#065F46' }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            overflowX: 'auto',
            py: 1,
            px: 0.5,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: color.ink[300], borderRadius: 3 }
          }}
        >
          {activeAddons.length === 0 ? (
            <Typography variant="body2" sx={{ color: color.ink[400], py: 3, mx: 'auto' }}>
              No active impulse add-ons. Click "Pin Accessory" to add recommendations.
            </Typography>
          ) : (
            activeAddons.map((item, idx) => (
              <Box
                key={item.id}
                sx={{
                  flex: '0 0 160px',
                  bgcolor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  p: 1.25,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
                }}
              >
                <Chip
                  label={item.badge_text}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    height: 16,
                    fontSize: '0.58rem',
                    fontWeight: 800,
                    bgcolor: color.primary[50],
                    color: color.primary[700]
                  }}
                />
                <Box
                  component="img"
                  src={getImageUrl(item.image_url) || getAccessoryFallback(item.product_name)}
                  alt={item.product_name}
                  sx={{
                    width: '100%',
                    height: 90,
                    objectFit: 'contain',
                    mt: 1.5,
                    mb: 1,
                    p: 0.5
                  }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = getAccessoryFallback(item.product_name);
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: color.ink[900],
                    lineHeight: 1.2,
                    height: 28,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    fontSize: '0.72rem'
                  }}
                >
                  {item.custom_title || item.product_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: color.ink[900], fontSize: '0.78rem' }}>
                    ₹{item.sale_price?.toLocaleString('en-IN')}
                  </Typography>
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.25,
                      borderRadius: '6px',
                      border: `1px solid ${color.primary[600]}`,
                      color: color.primary[600],
                      fontWeight: 800,
                      fontSize: '0.68rem',
                      cursor: 'pointer'
                    }}
                  >
                    + ADD
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Paper>

      {/* ── Table of Pinned Accessories ── */}
      <Panel>
        <DataTable
          columns={columns}
          rows={addons}
          loading={loading}
          emptyTitle="No cart impulse add-ons configured yet."
        />
      </Panel>

      {/* ── Add / Edit Dialog ── */}
      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSave}
        title={editingItem ? 'Edit Cart Add-On' : 'Pin Accessory to Cart Impulse Tray'}
        submitLabel={editingItem ? 'Save Changes' : 'Pin to Cart'}
        loading={saving}
        saving={saving}
        maxWidth="sm"
      >
        <Stack spacing={2.5}>
          {/* Step 1: Product Selection (only if adding) */}
          {!editingItem && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: color.ink[700], display: 'block', mb: 0.75 }}>
                1. Search Accessory from Catalog (Category: Accessories) *
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search accessories (e.g. 20W Adapter, Power Bank, Cable, Earphones)..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon fontSize="small" sx={{ color: color.ink[400], mr: 1 }} />
                  ),
                  endAdornment: searchLoading && <CircularProgress size={16} />
                }}
              />

              {searchResults.length > 0 && (
                <Paper variant="outlined" sx={{ mt: 1, maxHeight: 200, overflowY: 'auto', borderRadius: '8px' }}>
                  <List dense disablePadding>
                    {searchResults.map((p) => (
                      <ListItem
                        key={p.id}
                        button
                        selected={selectedProduct?.id === p.id}
                        onClick={() => {
                          setSelectedProduct(p);
                          setCustomTitle(p.name);
                        }}
                        sx={{
                          '&.Mui-selected': { bgcolor: color.primary[50] },
                          borderBottom: '1px solid #F3F4F6'
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={getImageUrl(p.primaryImage)}
                            variant="rounded"
                            sx={{ width: 32, height: 32, bgcolor: '#F9FAFB' }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>}
                          secondary={`${p.brand || 'Hotspot'} • ₹${p.salePrice?.toLocaleString('en-IN')}`}
                        />
                        <ListItemSecondaryAction>
                          <Button
                            size="small"
                            variant={selectedProduct?.id === p.id ? 'contained' : 'outlined'}
                            onClick={() => {
                              setSelectedProduct(p);
                              setCustomTitle(p.name);
                            }}
                            sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem' }}
                          >
                            {selectedProduct?.id === p.id ? 'Selected' : 'Select'}
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          )}

          {/* Selected Product Card */}
          {selectedProduct && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: '10px',
                bgcolor: '#F9FAFB',
                borderColor: color.primary[200],
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <Box
                component="img"
                src={getImageUrl(selectedProduct.primaryImage) || getAccessoryFallback(selectedProduct.name)}
                alt={selectedProduct.name}
                sx={{ width: 44, height: 44, objectFit: 'contain', borderRadius: '6px', bgcolor: '#FFFFFF', p: 0.5 }}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = getAccessoryFallback(selectedProduct.name);
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                  {selectedProduct.name}
                </Typography>
                <Typography variant="caption" sx={{ color: color.primary[700], fontWeight: 800 }}>
                  ₹{selectedProduct.salePrice?.toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Badge Text Customization */}
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: color.ink[700], display: 'block', mb: 0.75 }}>
              Impulse Badge Tag (Shown on card corner)
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
              {PRESET_BADGES.map((b) => (
                <Chip
                  key={b}
                  label={b}
                  size="small"
                  clickable
                  onClick={() => setBadgeText(b)}
                  color={badgeText === b ? 'primary' : 'default'}
                  variant={badgeText === b ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 700, fontSize: '0.68rem' }}
                />
              ))}
            </Stack>
            <TextField
              fullWidth
              size="small"
              value={badgeText}
              onChange={(e) => setBadgeText(e.target.value.toUpperCase())}
              placeholder="Custom badge (e.g. MUST HAVE, ESSENTIAL)"
            />
          </Box>

          {/* Custom Display Title */}
          <TextField
            fullWidth
            size="small"
            label="Custom Display Title (Optional)"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            helperText="Shorten the title if the catalog name is too long for the mobile card"
          />

          {/* Sort Order */}
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Display Sort Order (1 = leftmost card)"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </Stack>
      </FormDialog>

      {/* ── Delete Confirmation ── */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Remove Cart Add-On?"
        message="Are you sure you want to remove this product from the cart impulse tray? It will no longer be recommended to customers in the cart drawer."
        confirmLabel="Remove"
        danger
      />
    </Box>
  );
}
